import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getAreaListByBranchId, getKioskSettings, updateKioskSettings } from '../../../api/golf';
import { errorMessages } from '../../../utils/methods';
import { getAllStaffs, getStaffById } from '../../../api/staff';
import { useToast } from 'use-toast-mui';

const KioskSetting = ({ branchId }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [callMethod, setCallMethod] = useState('search'); // 'search' or 'direct'
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [directName, setDirectName] = useState('');
  const [directPhone, setDirectPhone] = useState('');
  const [spotArea, setSpotArea] = useState('');
  const [spotLayoutImage, setSpotLayoutImage] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null); // 선택된 직원 ID (프로필 이미지 조회용)
  const toast = useToast();

  const spotLayoutInputRef = useRef(null);
  const userBranch = useSelector(function (state) {
    return state.user.user.branch;
  });
  const userHead = useSelector(function (state) {
    return state.user.user.headBranch;
  });

  const initialQuery = {
    page: 1,
    limit: 10,
    headBranchId: userHead?.headBranchId,
    branchId: userBranch?.branchId,
    searchInput: '',
    responsibilities: [],
    classifications: [],
  };
  var qs = require('qs');

  const [query, setQuery] = useState(initialQuery);
  const queryString = qs.stringify(query);

  // 키오스크 설정 조회
  const { data: kioskSettingsData } = useQuery({
    queryKey: ['getKioskSettings', user?.token, branchId],
    queryFn: () => getKioskSettings(user.token, branchId),
    enabled: !!user?.token && !!branchId,
  });


  // kioskSettingsData가 로드되면 초기 데이터 설정
  useEffect(() => {
    if (kioskSettingsData) {
      // 직원 데이터 설정
      if (kioskSettingsData.employees && Array.isArray(kioskSettingsData.employees)) {
        const formattedEmployees = kioskSettingsData.employees.map(emp => ({
          id: emp.employeeId || emp.id,
          name: emp.employeeName || '',
          phone: emp.phoneNumber || '',
          profileImage: emp.profileImageUrl || '/images/kiosk/fb332ffbcb3b4eec331a09f7a981394b8832c4e7.png',
        }));
        setSelectedEmployees(formattedEmployees);
      }

      // 구역 설정 (첫 번째 areaLayoutImages의 areaId로 초기 설정)
      if (
        kioskSettingsData.areaLayoutImages &&
        Array.isArray(kioskSettingsData.areaLayoutImages) &&
        kioskSettingsData.areaLayoutImages.length > 0
      ) {
        const firstAreaImage = kioskSettingsData.areaLayoutImages[0];
        if (firstAreaImage.areaId) {
          // 초기값 설정
          setSpotArea(String(firstAreaImage.areaId));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskSettingsData]);

  // spotArea가 변경되거나 kioskSettingsData가 로드되면 해당하는 이미지 설정
  useEffect(() => {
    if (kioskSettingsData && spotArea) {
      // 선택된 spotArea와 일치하는 이미지 찾기
      const matchedImage = kioskSettingsData.areaLayoutImages?.find(img => String(img.areaId) === String(spotArea));

      if (matchedImage && matchedImage.layoutImageUrl) {
        // 해당 areaId에 이미지가 있으면 설정 (layoutImageUrl 필드 사용)
        setSpotLayoutImage(matchedImage.layoutImageUrl);
      } else {
        // 해당 areaId에 이미지가 없으면 초기화
        setSpotLayoutImage(null);
        if (spotLayoutInputRef.current) {
          spotLayoutInputRef.current.value = '';
        }
      }
    }
  }, [spotArea, kioskSettingsData]);

  // 스팟 구역 목록 조회
  const { data: spotAreasData = [] } = useQuery({
    queryKey: ['getAreaListByBranchId', user?.token, branchId],
    queryFn: () => getAreaListByBranchId(user.token, branchId),
    enabled: !!user?.token && !!branchId,
    select: data => (Array.isArray(data) ? data : []),
  });

  const spotAreas = spotAreasData || [];

  // 임직원 목록 조회
  const { data: employeesResponse } = useQuery({
    queryKey: ['getAllStaffs', user?.token, queryString],
    queryFn: () => getAllStaffs(user.token, queryString),
    enabled: !!user?.token && !!branchId,
  });

  // API 응답에서 직원 배열 추출 (content 배열 또는 전체 배열) - useMemo로 메모이제이션
  const employeesData = useMemo(() => {
    return employeesResponse?.content || (Array.isArray(employeesResponse) ? employeesResponse : []);
  }, [employeesResponse]);

  // 선택된 직원의 상세 정보 조회 (프로필 이미지 가져오기)
  const { data: selectedStaffData } = useQuery({
    queryKey: ['getStaffById', user?.token, selectedEmployeeId],
    queryFn: () => getStaffById(selectedEmployeeId, user.token),
    enabled: !!user?.token && !!selectedEmployeeId && !selectedEmployeeId.toString().startsWith('direct-'),
  });

  // 직원 검색 핸들러
  const handleEmployeeSearch = value => {
    setSearchInput(value);

    if (!value || value.trim().length === 0) {
      setSearchResults([]);
      // 검색어가 비어있으면 query 초기화
      setQuery(prev => ({ ...prev, searchInput: '', page: 1 }));
      return;
    }

    // 검색어로 query 업데이트 (API 재호출)
    setQuery(prev => ({ ...prev, searchInput: value, page: 1 }));
  };

  // employeesData가 변경될 때 검색 결과 업데이트
  useEffect(() => {
    if (searchInput && employeesData.length > 0) {
      // 직원 목록에서 검색어로 필터링 (이름 또는 전화번호)
      const filtered = employeesData.filter(employee => {
        const name = employee.name || '';
        const phone = employee.phoneNumber || '';
        const searchLower = searchInput.toLowerCase();
        return (
          name.toLowerCase().includes(searchLower) || phone.replace(/-/g, '').includes(searchLower.replace(/-/g, ''))
        );
      });

      // 검색 결과를 Autocomplete 형식으로 변환
      const formattedResults = filtered.map(employee => ({
        id: employee.id || employee.employeeId,
        name: employee.name || '',
        phone: employee.phoneNumber || '',
      }));

      setSearchResults(formattedResults);
    } else if (!searchInput) {
      setSearchResults([]);
    }
  }, [employeesData, searchInput]);

  // 선택된 직원의 프로필 이미지가 로드되면 selectedEmployees에 추가
  useEffect(() => {
    if (selectedStaffData && selectedEmployeeId) {
      // 함수형 업데이트로 최신 상태 사용
      setSelectedEmployees(prevEmployees => {
        // 이미 추가된 직원인지 확인
        const isAlreadyAdded = prevEmployees.some(emp => emp.id === selectedEmployeeId);

        if (isAlreadyAdded) {
          toast.error('이미 선택된 직원입니다.');
          setSelectedEmployeeId(null); // 이미 추가된 경우 초기화
          return prevEmployees;
        }

        // 검색 결과에서 해당 직원 찾기
        const employeeFromSearch = searchResults.find(emp => emp.id === selectedEmployeeId);

        if (employeeFromSearch) {
          // 프로필 이미지 포함하여 추가
          const employeeWithProfile = {
            ...employeeFromSearch,
            profileImage:
              selectedStaffData.profileImage || '/images/kiosk/fb332ffbcb3b4eec331a09f7a981394b8832c4e7.png',
          };

          setSearchInput('');
          setSearchResults([]);
          setSelectedEmployeeId(null); // 초기화

          const newEmployees = [...prevEmployees, employeeWithProfile];
          return newEmployees;
        }
        return prevEmployees;
      });
    }
  }, [selectedStaffData, selectedEmployeeId, searchResults, toast]);

  // 직원 선택 핸들러
  const handleEmployeeSelect = (event, value) => {
    if (!value) return;

    // 최대 인원 체크
    if (selectedEmployees.length >= 5) {
      toast.error('최대 5명까지 선택할 수 있습니다.');
      return;
    }

    // 중복 체크
    const isAlreadySelected = selectedEmployees.some(emp => emp.id === value.id);
    if (isAlreadySelected) {
      toast.error('이미 선택된 직원입니다.');
      return;
    }

    // 직접 입력한 직원이 아닌 경우에만 상세 정보 조회
    if (value.id && !value.id.toString().startsWith('direct-')) {
      setSelectedEmployeeId(value.id);
    } else {
      // 직접 입력한 직원은 바로 추가
      setSelectedEmployees([...selectedEmployees, value]);
      setSearchInput('');
      setSearchResults([]);
    }
  };

  // 직원 제거 핸들러
  const handleRemoveEmployee = employeeId => {
    setSelectedEmployees(selectedEmployees.filter(emp => emp.id !== employeeId));
  };

  // 전화번호 포맷팅 함수 (010-1111-2222 형식)
  const formatPhoneNumber = value => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '');

    // 길이에 따라 포맷팅
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // 전화번호 입력 핸들러
  const handlePhoneChange = e => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setDirectPhone(formatted);
  };

  // 직접 입력으로 직원 추가 핸들러
  const handleAddDirectEmployee = () => {
    if (!directName.trim() || !directPhone.trim()) {
      alert('이름과 전화번호를 모두 입력해주세요.');
      return;
    }

    if (selectedEmployees.length >= 5) {
      alert('최대 5명까지 선택할 수 있습니다.');
      return;
    }

    // 전화번호 중복 체크 (하이픈 제거 후 비교)
    const phoneWithoutHyphen = directPhone.replace(/-/g, '');
    const isDuplicate = selectedEmployees.some(emp => {
      const empPhoneWithoutHyphen = emp.phone.replace(/-/g, '');
      return empPhoneWithoutHyphen === phoneWithoutHyphen;
    });

    if (isDuplicate) {
      toast.error('이미 추가된 전화번호입니다.');
      return;
    }

    // 직접 입력한 직원 추가 (임시 ID 생성, 하이픈 포함)
    const newEmployee = {
      id: `direct-${Date.now()}`,
      name: directName.trim(),
      phone: directPhone, // 하이픈 포함하여 저장
    };

    setSelectedEmployees([...selectedEmployees, newEmployee]);
    setDirectName('');
    setDirectPhone('');
  };

  // 타석 배치도 이미지 업로드 핸들러
  const handleSpotLayoutImageUpload = event => {
    const file = event.target.files[0];
    if (file) {
      // 파일 크기 체크 (2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('파일 크기는 2MB 이하여야 합니다.');
        return;
      }
      // 이미지 크기 체크는 서버에서 처리하거나, 이미지 로드 후 체크
      setSpotLayoutImage(file);
    }
  };

  const KioskSettingMutation = useMutation({
    mutationFn: data => updateKioskSettings(user.token, data),
    onSuccess: () => {
      toast.success('설정이 저장되었습니다.');
    },
    onError: error => {
      // 서버 사유(예: 직원 5명 초과)를 그대로 보여준다
      toast.error(errorMessages(error) || '설정 저장에 실패했습니다.');
    },
  });

  // 설정 저장 핸들러
  const handleSave = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('직원을 선택해주세요.');
      return;
    }

    // 구역·배치도는 선택 사항. 직원만 바꿔도 저장할 수 있어야 한다 (백엔드는 이미지 없으면 기존 것을 유지한다).
    // 새 이미지 파일을 골랐을 때만 어느 구역 것인지 필요하다.
    const isNewImageFile = spotLayoutImage instanceof File || spotLayoutImage instanceof Blob;
    if (isNewImageFile && !spotArea) {
      toast.error('구역을 선택해주세요.');
      return;
    }

    // FormData 생성
    const formData = new FormData();

    // branchId 추가
    formData.append('branchId', Number(branchId));

    // employees 배열 추가
    selectedEmployees.forEach((employee, index) => {
      formData.append(
        `employees[${index}].employeeId`,
        employee.id && !employee.id.toString().startsWith('direct-') ? Number(employee.id) : 0,
      );
      formData.append(`employees[${index}].employeeName`, employee.name || '');
      formData.append(`employees[${index}].phoneNumber`, employee.phone || '');
      formData.append(
        `employees[${index}].profileImageUrl`,
        employee.profileImage || '/images/kiosk/fb332ffbcb3b4eec331a09f7a981394b8832c4e7.png',
      );
      formData.append(`employees[${index}].sortOrder`, index);
    });

    // battingPositionImages — 새로 고른 파일이 있을 때만 전송. 기존 URL 을 다시 받아 재업로드하던 경로는 제거
    if (isNewImageFile && spotArea) {
      const selectedArea = spotAreas.find(area => String(area.golfAreaId) === String(spotArea));
      formData.append('battingPositionImages[0].areaId', Number(spotArea));
      formData.append('battingPositionImages[0].areaName', selectedArea?.golfAreaInfo?.areaName || '');
      // 서버가 파일을 받을 때 사용하는 필드명: file
      formData.append('battingPositionImages[0].file', spotLayoutImage);
    } else {
      // 빈 배열을 명시적으로 보내기 위해 빈 필드 추가
      formData.append('battingPositionImages[]', '');
    }
    KioskSettingMutation.mutate(formData);
  };

  // 이미지 미리보기 URL 생성 일단 임시
  const getImagePreview = file => {
    if (!file) return '';
    // 이미 문자열(blob URL 또는 일반 URL)인 경우 그대로 사용
    if (typeof file === 'string') {
      // 절대 URL인 경우 그대로 사용
      if (
        file.startsWith('http://') ||
        file.startsWith('https://') ||
        file.startsWith('//') ||
        file.startsWith('blob:')
      ) {
        return file;
      }
      // 상대 경로인 경우 그대로 사용 (이미 API에서 받은 URL일 가능성이 높음)
      return file;
    }
    // File 객체인 경우 blob URL 생성
    if (file instanceof File || file instanceof Blob) {
      return URL.createObjectURL(file);
    }
    return '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', padding: '26px 32px', alignItems: 'center' }}>
      {/* 메인 컨텐츠 박스 */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #D9D9D9',
          borderRadius: '8px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
          maxWidth: '1472px',
        }}>
        {/* 직원 호출 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{t('Call employee')}</Typography>
            <Typography sx={{ fontSize: '12px', color: '#B3B3B3' }}>
              {t('Call employee description')}
              <span style={{ color: '#EE735A' }}>({t('Maximum number of employees')})</span>
            </Typography>
          </Box>

          {/* 라디오 버튼 그룹 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* 직원검색 옵션 */}
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '4px 0' }}>
              <RadioGroup row value={callMethod} onChange={e => setCallMethod(e.target.value)} sx={{ margin: 0 }}>
                <FormControlLabel
                  value="search"
                  control={<Radio size="small" />}
                  label=""
                  sx={{ margin: 0, marginRight: '12px' }}
                />
              </RadioGroup>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', minWidth: '64px' }}>
                {t('Search employee')}
              </Typography>
              {callMethod === 'search' && (
                <Autocomplete
                  sx={{
                    width: '220px',
                    '& .MuiOutlinedInput-root': {
                      padding: '0 !important',
                    },
                  }}
                  options={searchResults}
                  getOptionLabel={option => `${option.name} (${option.phone})`}
                  onInputChange={(event, newInputValue) => handleEmployeeSearch(newInputValue)}
                  onChange={handleEmployeeSelect}
                  value={null}
                  inputValue={searchInput}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={t('Search employee')}
                      size="small"
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                          fontSize: '14px',
                          paddingRight: '12px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <SearchIcon sx={{ color: '#B3B3B3', fontSize: '20px', marginRight: '12px' }} />,
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name} ({option.phone})
                    </li>
                  )}
                  noOptionsText={t('No search results`')}
                />
              )}
            </Box>

            {/* 직접입력 옵션 */}
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <RadioGroup row value={callMethod} onChange={e => setCallMethod(e.target.value)} sx={{ margin: 0 }}>
                <FormControlLabel
                  value="direct"
                  control={<Radio size="small" />}
                  label=""
                  sx={{ margin: 0, marginRight: '12px' }}
                />
              </RadioGroup>
              {callMethod !== 'direct' && (
                <Typography
                  sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', minWidth: '64px', marginTop: '8px' }}>
                  {t('Direct input')}
                </Typography>
              )}
              {callMethod === 'direct' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  {/* 이름 입력 */}
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', minWidth: '64px' }}>
                      {t('Name')}
                    </Typography>
                    <TextField
                      placeholder="홍길동"
                      value={directName}
                      onChange={e => setDirectName(e.target.value)}
                      size="small"
                      sx={{
                        width: '80px',
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                        },
                      }}
                    />
                  </Box>
                  {/* 전화번호 입력 */}
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#262626', minWidth: '64px' }}>
                      {t('Phone Number')}
                    </Typography>
                    <TextField
                      placeholder="010-1234-5678"
                      value={directPhone}
                      onChange={handlePhoneChange}
                      size="small"
                      sx={{
                        width: '160px',
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                        },
                      }}
                    />
                    <Button
                      onClick={handleAddDirectEmployee}
                      sx={{
                        backgroundColor: '#262626',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        fontWeight: 500,
                        padding: '8px 16px',
                        borderRadius: '4px',
                        height: '40px',
                        minWidth: '60px',
                        '&:hover': {
                          backgroundColor: '#3a3a3a',
                        },
                      }}>
                      {t('Add')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* 선택된 직원 태그들 */}
          {selectedEmployees.length > 0 && (
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              {selectedEmployees.map(employee => (
                <Chip
                  key={employee.id}
                  avatar={
                    <Avatar
                      sx={{ width: '24px', height: '24px' }}
                      src={employee.profileImage || '/images/kiosk/fb332ffbcb3b4eec331a09f7a981394b8832c4e7.png'}
                    />
                  }
                  label={`${employee.name}(${employee.phone})`}
                  onDelete={() => handleRemoveEmployee(employee.id)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '12px' }} />}
                  sx={{
                    border: '1px solid #A6A6A6',
                    borderRadius: '999px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#262626',
                    '& .MuiChip-deleteIcon': {
                      color: '#262626',
                      fontSize: '12px',
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* 타석 배치도 이미지 섹션 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
              {t('Spot layout image')}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#B3B3B3' }}>
              {t('Spot layout image description')}{' '}
              <span style={{ color: '#EE735A' }}>{t('Spot layout image size')}</span>
            </Typography>
          </Box>

          {/*  구역 선택 드롭다운 */}
          <FormControl size="small" sx={{ width: '240px' }}>
            <Select
              value={spotArea}
              onChange={e => setSpotArea(e.target.value)}
              displayEmpty
              sx={{
                height: '40px',
                fontSize: '14px',
                '& .MuiSelect-select': {
                  padding: '12px 16px',
                },
              }}>
              <MenuItem value="" disabled>
                {t('Please select the spot area')}
              </MenuItem>
              {spotAreas.map(area => (
                <MenuItem key={area.golfAreaId} value={area.golfAreaId}>
                  {area.golfAreaInfo?.areaName || ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 이미지 업로드 영역 */}
          <Box sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            {/* 업로드 버튼 */}
            <Box
              component="label"
              htmlFor="spot-layout-upload"
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #D9D9D9',
                borderRadius: '4px',
                width: '180px',
                height: '180px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#0F7F85',
                },
              }}>
              <input
                ref={spotLayoutInputRef}
                id="spot-layout-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleSpotLayoutImageUpload}
              />
              <CloudUploadIcon sx={{ fontSize: '28px', color: '#7DC8CF' }} />
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#7DC8CF' }}>{t('Upload image')}</Typography>
            </Box>

            {/* 업로드된 이미지 미리보기 */}
            {spotLayoutImage && getImagePreview(spotLayoutImage) && (
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  borderRadius: '4px',
                  width: '180px',
                  height: '180px',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                <img
                  src={getImagePreview(spotLayoutImage)}
                  alt="타석 배치도"
                  onError={e => {
                    console.error('이미지 로드 실패:', e);
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Button
                  onClick={() => {
                    setSpotLayoutImage(null);
                    if (spotLayoutInputRef.current) {
                      spotLayoutInputRef.current.value = '';
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    minWidth: 'auto',
                    padding: '4px',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}>
                  <CloseIcon sx={{ fontSize: '20px' }} />
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 설정저장 버튼 */}
      <Button
        onClick={handleSave}
        sx={{
          backgroundColor: '#0F7F85',
          color: '#FFFFFF',
          fontSize: '20px',
          fontWeight: 600,
          padding: '12px 24px',
          borderRadius: '8px',
          width: '240px',
          height: '48px',
          '&:hover': {
            backgroundColor: '#0a6a70',
          },
        }}>
        {t('Save settings')}
      </Button>
    </Box>
  );
};

export default KioskSetting;
